import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '~/lib/cn';

export type ButtonVariant =
  | 'primary' // Angell blue: the action a person is being asked to take
  | 'ink' // dark: Ask, New conversation, Run it
  | 'outline' // quiet secondary
  | 'outline-blue' // Copy as email in a thread
  | 'filed' // Mark as filed (outline, filed ink)
  | 'filed-solid' // Mark filed and lock
  | 'reviewed' // Mark reviewed when ready
  | 'disabled-look'; // Mark reviewed while blocked

export type ButtonSize = 32 | 34 | 36 | 38 | 40 | 42 | 44 | 56;

const variants: Record<ButtonVariant, string> = {
  primary: 'border-0 bg-blue text-white font-bold hover:bg-blue-ink',
  ink: 'border-0 bg-ink text-white font-bold hover:bg-filed',
  outline: 'border border-line-strong bg-surface text-text hover:bg-paper',
  'outline-blue': 'border border-blue bg-surface text-blue font-semibold hover:bg-blue-tint',
  filed: 'border border-filed bg-surface text-filed font-bold hover:bg-sunk',
  'filed-solid': 'border-0 bg-filed text-white font-bold hover:bg-ink',
  reviewed: 'border-0 bg-green-ink text-white font-bold',
  'disabled-look': 'border border-line-strong bg-sunk text-muted font-bold',
};

const sizes: Record<ButtonSize, { h: string; pad: string; text: string }> = {
  32: { h: 'min-h-8', pad: 'px-2.5', text: 'text-13' },
  34: { h: 'min-h-[34px]', pad: 'px-3', text: 'text-13' },
  36: { h: 'min-h-9', pad: 'px-3', text: 'text-14' },
  38: { h: 'min-h-[38px]', pad: 'px-3.5', text: '' },
  40: { h: 'min-h-10', pad: 'px-3.5', text: '' },
  42: { h: 'min-h-[42px]', pad: 'px-4.5', text: '' },
  44: { h: 'min-h-11', pad: 'px-4.5', text: '' },
  56: { h: 'min-h-14', pad: 'px-5', text: 'text-17' },
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** padding override, e.g. "px-4" */
  pad?: string;
  /** corner radius override; buttons are 6px unless the design says 8px */
  radius?: 'md' | 'lg';
};

/** The one button. Words always say what happens: "Save correction", not "Submit". */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'outline', size = 40, pad, radius = 'md', className, type = 'button', ...rest },
  ref,
) {
  const s = sizes[size];
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 transition-colors disabled:cursor-not-allowed',
        radius === 'lg' ? 'rounded-lg' : 'rounded-md',
        variants[variant],
        s.h,
        pad ?? s.pad,
        s.text,
        className,
      )}
      {...rest}
    />
  );
});
